import { useCallback, useMemo } from 'react';
import { CustomMessageBar } from '@shared/components';
import { Button } from '@fluentui/react-components';
import RefModeTabs from './RefModeTabs';
import { useGroupedRefs } from '../../hooks/useGroupedRefs.hook';
import { useNavigate } from 'react-router-dom';
import { TFolder, TPath, TResolvedItemsMap } from '@modules/items';
import { FULL_PATH_SETUP_ITEM } from '@modules/items';
import { FULL_PATH_REFERENCE_DETECTION_ITEM } from '../../constants';
import DetectionScaffold from '../shared/layout/DetectionScaffold';
import SummaryTopBar from '../shared/layout/SummaryTopBar';
import SummarySectionsList from '../shared/layout/SummarySectionsList';
import { IReferenceDetectionRun } from '../../types/references.types';
import { TERMINOLOGY } from '@shared/i18n';

type TSummaryContentProps = {
  run: IReferenceDetectionRun;
  resolvedItems: TResolvedItemsMap;
  folder: TFolder;
};
/**
 * Displays inserted references, grouped by source document
 */

const ReferenceDetectionSummaryContent = ({ run, resolvedItems, folder }: TSummaryContentProps) => {
  const navigate = useNavigate();
  const handleStartNewDetection = () => {
    navigate(`/product/${run.rootItem}/reference-detection`);
  };

  // stable filter declared **once** so it doesn’t break memo deps
  const onlyInserted = useCallback((r: { inserted: boolean }) => !!r.inserted, []);

  const { selectedTab, setSelectedTab, groupedRefs, groupedByType } = useGroupedRefs(
    run.detectedReferences,
    run.detectedSelfReferences,
    {
      filter: onlyInserted,
    }
  );

  const totalRefs = groupedByType.referencesCount + groupedByType.selfReferencesCount;
  // total number of unique items
  const totalItems = new Set([...groupedByType.references.keys(), ...groupedByType.selfReferences.keys()]).size;
  // `Object.entries` is O(n) operation, so we memoize it
  const groupedEntries = useMemo(() => Object.entries(groupedRefs), [groupedRefs]);

  // "fullPath" for the FoldersNavDrawer
  const fullPath: TPath = [
    { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
    FULL_PATH_SETUP_ITEM, // Setup
    FULL_PATH_REFERENCE_DETECTION_ITEM, // Reference detection
  ];

  return (
    <DetectionScaffold
      folder={folder}
      fullPath={fullPath}
      selectedValue="reference-detection"
      maxWidth="max-w-screen-xl"
    >
      <SummaryTopBar
        left={<RefModeTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} groupedByType={groupedByType} />}
        success={{
          count: totalRefs,
          docs: totalItems,
          nounSingular: TERMINOLOGY.reference.lower,
        }}
      />

      {groupedEntries.length === 0 ? (
        <CustomMessageBar
          intent="info"
          title={`No ${TERMINOLOGY.reference.lowerPlural} found.`}
          body="Please make sure you have selected the correct folders and run the detection again."
        />
      ) : (
        <SummarySectionsList groupedEntries={groupedEntries} resolvedItems={resolvedItems} variant="references" />
      )}

      <div className="flex w-full justify-end py-3">
        <Button appearance="secondary" size="medium" onClick={handleStartNewDetection} className="w-fit">
          Start new detection
        </Button>
      </div>
    </DetectionScaffold>
  );
};

export default ReferenceDetectionSummaryContent;
